import cursorIncludedModels from "./cursor-included-models.json";
import React, { useEffect, useRef, useState } from "react";

export function isAllowedCursorReviewer(provider: string) {
  return !provider.startsWith("cursor:") || cursorIncludedModels.includes(provider.slice(7));
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type UsagePool = {
  id: string;
  label: string;
  remaining_pct?: number;
  summary?: string;
  hard_exhausted?: boolean;
};

export type UsageEnvelope = {
  data?: {
    stale?: boolean;
    refresh_error?: string;
    remaining_pct?: number;
    summary?: string;
    reset_label?: string;
    pool?: string;
    pools?: UsagePool[];
  };
};

type ProviderUsageSnapshot = {
  legacy_analytics?: {
    live_usage?: Record<string, UsageEnvelope>;
  };
};

const REVIEWER_PIN_LABELS: Record<string, string> = {
  grok: "Grok",
  codex: "Codex",
  "cursor-grok": "Cursor Grok",
  "cursor-claude": "Cursor Claude",
  "cursor-gpt": "Cursor GPT",
  "cursor-glm": "Cursor GLM",
  "cursor-kimi": "Cursor Kimi",
};

function cursorFamilyFromProvider(provider: string) {
  if (!provider.startsWith("cursor:")) return undefined;
  const model = provider.slice("cursor:".length);
  if (model.startsWith("cursor-grok") || model.startsWith("grok")) return "cursor-grok";
  if (model.startsWith("composer")) return "cursor-composer";
  if (model.startsWith("claude")) return "cursor-claude";
  if (model.startsWith("gpt") || model.startsWith("o1") || model.startsWith("o3")) return "cursor-gpt";
  if (model.startsWith("glm")) return "cursor-glm";
  if (model.startsWith("kimi")) return "cursor-kimi";
  return "cursor-other";
}

export const NATIVE_REVIEWER_CATALOG: ReviewerCatalogEntry[] = [
  { id: "grok", label: "Grok", group: "Native" },
  { id: "codex", label: "Codex", group: "Native" },
  { id: "claude", label: "Claude", group: "Native" },
  { id: "cursor", label: "Cursor", group: "Native" },
];

export function reviewerNative(provider: string) {
  if (provider === "grok" || provider.startsWith("grok:")) return "grok";
  if (provider === "codex" || provider.startsWith("codex:")) return "codex";
  if (provider === "claude" || provider.startsWith("claude:")) return "claude";
  if (provider === "cursor" || provider.startsWith("cursor:")) return "cursor";
  return undefined;
}

export function reviewerPoolLabel(provider: string, catalogLabel?: string) {
  if (catalogLabel) return catalogLabel;
  if (provider === "grok" || provider === "codex" || provider === "claude" || provider === "cursor") {
    return providerLabel(provider);
  }
  const separator = provider.indexOf(":");
  if (separator > 0) return provider.slice(separator + 1);
  return providerLabel(provider);
}

export function providerLabel(provider: string) {
  if (provider === "cursor") return "Cursor";
  if (REVIEWER_PIN_LABELS[provider]) return REVIEWER_PIN_LABELS[provider];
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

export function normalizeReviewerPin(value: string) {
  if (value === "auto" || value === "grok" || value === "codex" || value === "claude" || value === "cursor") {
    return value;
  }
  if (value.startsWith("grok:") || value.startsWith("grok-")) return "grok";
  if (value.startsWith("codex:")) return "codex";
  if (value.startsWith("claude:") || value.startsWith("claude-")) return "claude";
  if (value.startsWith("cursor:") || value.startsWith("cursor-")) {
    return "cursor";
  }
  return "auto";
}

export function toggleReviewerPool(
  installed: string[],
  active: string[],
  provider: string,
  allowEmpty = false,
) {
  const current = (active.length > 0 || allowEmpty ? active : installed).filter((item) => installed.includes(item));
  if (current.includes(provider)) {
    const next = current.filter((item) => item !== provider);
    return next.length > 0 || allowEmpty ? next : current;
  }
  return [...current, provider];
}

export function moveReviewer(active: string[], provider: string, delta: number) {
  const index = active.indexOf(provider);
  if (index < 0) return active;
  const nextIndex = index + delta;
  if (nextIndex < 0 || nextIndex >= active.length) return active;
  const next = [...active];
  const [item] = next.splice(index, 1);
  next.splice(nextIndex, 0, item);
  return next;
}

export function dispatchReviewers(natives: string[], selected: string[]) {
  const nativeSet = new Set(natives);
  const dispatch: string[] = [];
  const used = new Set<string>();
  for (const provider of selected) {
    const native = reviewerNative(provider);
    if (!native || !nativeSet.has(native) || !provider.includes(":") || !isAllowedCursorReviewer(provider)) continue;
    dispatch.push(provider);
    used.add(native);
  }
  for (const native of natives) {
    if (!used.has(native) && native !== "cursor") dispatch.push(native);
  }
  return dispatch;
}

export function defaultActiveNatives(providers: string[]) {
  return NATIVE_REVIEWER_CATALOG
    .map((entry) => entry.id)
    .filter((native) => providers.some((provider) => reviewerNative(provider) === native));
}

export function selectedModelIds(providers: string[]) {
  return providers.filter((provider) => provider.includes(":"));
}

export const REVIEW_LEVELS = ["deep", "standard", "fast"] as const;
export type ReviewLevel = (typeof REVIEW_LEVELS)[number];

export function reviewModelTier(provider: string): ReviewLevel {
  const model = (provider.includes(":") ? provider.slice(provider.indexOf(":") + 1) : provider).toLowerCase();
  if (
    model.startsWith("glm")
    || model.includes("flash")
    || model.includes("luna")
    || model.includes("haiku")
    || model.includes("mini")
    || model.includes("nano")
    || model.includes("spark")
    || model.startsWith("composer")
  ) {
    return "fast";
  }
  if (model.includes("sol") || model.includes("opus") || model.includes("fable") || (model.includes("kimi") && model.includes("max"))) {
    return "deep";
  }
  if (model.includes("grok-4.6")) {
    if (model.includes("low")) return "fast";
    if (model.includes("medium")) return "standard";
    return "deep";
  }
  if (model.includes("grok")) return model.includes("low") ? "fast" : "standard";
  if (model.includes("terra") || model.includes("sonnet") || model.startsWith("kimi") || (model.includes("gemini") && model.includes("pro"))) {
    return "standard";
  }
  if (model.includes("low")) return "fast";
  if (model.includes("xhigh") || model.includes("max")) return "deep";
  return "standard";
}

export function modelsForReviewLevel(providers: string[], level: ReviewLevel) {
  return selectedModelIds(providers).filter((provider) => reviewModelTier(provider) === level);
}

export function unionReviewerModels(...lists: Array<string[] | undefined>) {
  const seen = new Set<string>();
  const models: string[] = [];
  for (const list of lists) {
    for (const provider of list ?? []) {
      if (provider.includes(":") && !seen.has(provider)) {
        seen.add(provider);
        models.push(provider);
      }
    }
  }
  return models;
}

export function resolveReviewerSettings(settings?: {
  providers?: string[];
  active_natives?: string[];
  active_providers?: string[];
  active_providers_deep?: string[];
  active_providers_standard?: string[];
  active_providers_fast?: string[];
} | null) {
  const providers = settings?.active_providers ?? settings?.providers ?? [];
  const natives = settings?.active_natives ?? defaultActiveNatives(providers);
  const models = {
    deep: settings?.active_providers_deep ?? modelsForReviewLevel(providers, "deep"),
    standard: settings?.active_providers_standard ?? modelsForReviewLevel(providers, "standard"),
    fast: settings?.active_providers_fast ?? modelsForReviewLevel(providers, "fast"),
  };
  return { natives, models, providers };
}

export type ReviewerCatalogEntry = {
  id: string;
  label: string;
  group: string;
  custom?: boolean;
};

const REVIEWER_NATIVES = ["grok", "codex", "claude", "cursor"] as const;

export function isPlainModelId(model: string) {
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(model);
}

function guessReviewerNative(model: string, natives: readonly string[]) {
  const lower = model.toLowerCase();
  const pick = (...names: string[]) => names.find((name) => natives.includes(name));
  if (lower.startsWith("claude")) return pick("claude", "cursor");
  if (lower.startsWith("grok")) return pick("grok", "cursor");
  if (lower.startsWith("gpt") || lower.startsWith("o1") || lower.startsWith("o3")) return pick("codex", "cursor");
  if (
    lower.startsWith("composer")
    || lower.startsWith("glm")
    || lower.startsWith("kimi")
    || lower.startsWith("gemini")
  ) return pick("cursor");
  return undefined;
}

/// Parse a picker query into a model that can be added for an installed native.
/// `claude:claude-haiku-5-5` pins the native. A bare id is guessed from its prefix.
export function reviewerAddition(query: string, natives: string[]) {
  const trimmed = query.trim();
  const allowed = natives.filter((native): native is (typeof REVIEWER_NATIVES)[number] =>
    REVIEWER_NATIVES.includes(native as (typeof REVIEWER_NATIVES)[number]));
  if (!trimmed || allowed.length === 0) return null;
  const colon = trimmed.indexOf(":");
  let explicitNative: string | undefined;
  let model = trimmed;
  if (colon > 0) {
    explicitNative = trimmed.slice(0, colon);
    model = trimmed.slice(colon + 1);
    if (!allowed.includes(explicitNative as (typeof REVIEWER_NATIVES)[number])) return null;
  }
  if (!isPlainModelId(model)) return null;
  const native = explicitNative ?? guessReviewerNative(model, allowed) ?? allowed[0];
  if (!isAllowedCursorReviewer(`${native}:${model}`)) return null;
  return { native, model, id: `${native}:${model}`, explicit: Boolean(explicitNative) };
}

export function reviewerCatalogFallback(providers: string[]): ReviewerCatalogEntry[] {
  return providers
    .filter((id) => id.includes(":") && isAllowedCursorReviewer(id))
    .map((id) => ({
      id,
      label: reviewerPoolLabel(id),
      group: reviewerNative(id) === "cursor" ? "Cursor" : providerLabel(reviewerNative(id) ?? "Installed"),
    }));
}

export function selectedReviewerEntries(catalog: ReviewerCatalogEntry[], active: string[]) {
  const byId = new Map(catalog.map((entry) => [entry.id, entry]));
  return active.filter(isAllowedCursorReviewer).map((id) => byId.get(id) ?? { id, label: reviewerPoolLabel(id), group: "Selected" });
}

export function ReviewerPoolDropdown({
  catalog,
  active,
  onChange,
  onForget,
  addNatives = [],
  searchable = true,
  allowEmpty = false,
  emptyLabel = "Select reviewers",
  searchPlaceholder = "Filter or add a model id",
}: {
  catalog: ReviewerCatalogEntry[];
  active: string[];
  onChange: (next: string[]) => void;
  onForget?: (id: string) => void;
  addNatives?: string[];
  searchable?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pinnedNative, setPinnedNative] = useState<{ query: string; native: string } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const suggestion = reviewerAddition(query, addNatives);
  const additionNative = pinnedNative?.query === query && addNatives.includes(pinnedNative.native)
    ? pinnedNative.native
    : (suggestion?.native ?? addNatives[0] ?? "claude");
  const selected = selectedReviewerEntries(catalog, active);
  const available = Array.from(new Set([...catalog.map((entry) => entry.id), ...active])).filter(isAllowedCursorReviewer);
  const additionId = suggestion && addNatives.includes(additionNative)
    && isAllowedCursorReviewer(`${additionNative}:${suggestion.model}`)
    ? `${additionNative}:${suggestion.model}`
    : null;
  const visible = query.trim()
    ? catalog.filter((entry) => `${entry.label} ${entry.id} ${entry.group}`.toLowerCase().includes(query.trim().toLowerCase()))
    : catalog;
  const groups = Array.from(new Set(visible.map((entry) => entry.group)));

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return <div className={`reviewer-pool${open ? " open" : ""}`} ref={root}>
    <button
      type="button"
      className="reviewer-pool-trigger"
      aria-expanded={open}
      aria-haspopup="listbox"
      onClick={() => setOpen((current) => !current)}
    >
      <span className="reviewer-pool-chips">
        {selected.length === 0
          ? <em>{emptyLabel}</em>
          : selected.map((entry, index) => (
            <span className="reviewer-pool-chip" key={entry.id}>
              {searchable ? <b>{index + 1}</b> : null}
              {entry.label}
            </span>
          ))}
      </span>
      <span className="reviewer-pool-caret" aria-hidden="true">{open ? "▴" : "▾"}</span>
    </button>
    {open && <div className="reviewer-pool-panel" role="listbox" aria-multiselectable="true">
      {searchable && <input
        className="reviewer-pool-search"
        type="search"
        value={query}
        placeholder={searchPlaceholder}
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
      />}
      {searchable && suggestion && additionId && !active.includes(additionId) && <div className="reviewer-pool-add">
        <select
          aria-label="CLI for the new model"
          value={additionNative}
          onChange={(event) => setPinnedNative({ query, native: event.target.value })}
        >
          {addNatives.map((native) => <option key={native} value={native}>{providerLabel(native)}</option>)}
        </select>
        <button
          type="button"
          onClick={() => {
            onChange([...active, additionId]);
            setQuery("");
          }}
        >Add {suggestion.model}</button>
      </div>}
      {searchable && selected.length > 1 && <div className="reviewer-pool-order">
        <strong>Priority</strong>
        {selected.map((entry, index) => (
          <div className="reviewer-pool-order-item" key={entry.id}>
            <span>{index + 1}. {entry.label}</span>
            <span className="reviewer-pool-order-actions">
              <button
                type="button"
                disabled={index === 0}
                aria-label={`Move ${entry.label} up`}
                onClick={() => onChange(moveReviewer(active, entry.id, -1))}
              >↑</button>
              <button
                type="button"
                disabled={index === selected.length - 1}
                aria-label={`Move ${entry.label} down`}
                onClick={() => onChange(moveReviewer(active, entry.id, 1))}
              >↓</button>
            </span>
          </div>
        ))}
      </div>}
      <div className="reviewer-pool-list">
        {groups.map((group) => (
          <div className="reviewer-pool-group" key={group}>
            <strong>{group}</strong>
            {visible.filter((entry) => entry.group === group).map((entry) => {
              const enabled = active.includes(entry.id);
              return <label key={entry.id} className="reviewer-pool-item" title={entry.id}>
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={enabled && active.length === 1 && !allowEmpty}
                  onChange={() => onChange(toggleReviewerPool(available, active, entry.id, allowEmpty))}
                />
                <span>{entry.label}</span>
                {entry.custom && onForget && <button
                  type="button"
                  className="reviewer-pool-remove"
                  aria-label={`Remove ${entry.label} from the model list`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onForget(entry.id);
                  }}
                >Remove</button>}
              </label>;
            })}
          </div>
        ))}
      </div>
    </div>}
  </div>;
}

export function providerPinOptions(providers: string[]) {
  const options: Array<{ value: string; label: string }> = [];
  if (providers.some((provider) => reviewerNative(provider) === "grok")) {
    options.push({ value: "grok", label: "Grok" });
  }
  if (providers.some((provider) => reviewerNative(provider) === "codex")) {
    options.push({ value: "codex", label: "Codex" });
  }
  if (providers.some((provider) => reviewerNative(provider) === "claude")) {
    options.push({ value: "claude", label: "Claude" });
  }
  if (providers.some((provider) => reviewerNative(provider) === "cursor")) {
    options.push({ value: "cursor", label: "Cursor" });
  }
  return options;
}

export function visibleUsageEntries(liveUsage: Record<string, UsageEnvelope> = {}) {
  const entries: Array<[string, UsageEnvelope]> = [];
  const natives = new Map<string, UsageEnvelope>();
  for (const [provider, envelope] of Object.entries(liveUsage)) {
    if (provider === "cursor" || provider.startsWith("cursor:")) {
      if (provider === "cursor") natives.set("cursor", envelope);
      continue;
    }
    const native = reviewerNative(provider) ?? provider;
    if (!natives.has(native) || provider === native) {
      natives.set(native, envelope);
    }
  }
  for (const native of ["grok", "codex", "claude", "cursor"]) {
    const envelope = natives.get(native);
    if (envelope) entries.push([native, envelope]);
    natives.delete(native);
  }
  for (const [provider, envelope] of natives) {
    entries.push([provider, envelope]);
  }
  return entries;
}

export function usageMeters(
  liveUsage: Record<string, UsageEnvelope> = {},
  activeProviders?: string[],
) {
  const active = activeProviders ? new Set(activeProviders) : undefined;
  const meters: Array<{ key: string; label: string; value: number; detail: string }> = [];
  for (const [provider, envelope] of visibleUsageEntries(liveUsage)) {
    if (provider === "cursor") continue;
    // Claude CLI quota stays visible even when reviews are not currently pinned to it.
    const selected = !active || provider === "claude" || [...active].some((item) => reviewerNative(item) === provider || item === provider);
    if (!selected) continue;
    const stale = envelope.data?.stale
      ? providerUsageStaleDetail(provider, envelope.data?.refresh_error)
      : undefined;
    meters.push({
      key: provider,
      label: providerLabel(provider),
      value: envelope.data?.remaining_pct ?? 0,
      detail: [
        envelope.data?.summary ?? "usage unavailable",
        envelope.data?.reset_label,
        stale,
      ].filter(Boolean).join(" · "),
    });
  }
  return meters;
}

function conciseRefreshError(provider: string, error?: string) {
  if (!error) return "no current usage snapshot";
  const prefix = `${providerLabel(provider)} `;
  return error.startsWith(prefix) ? error.slice(prefix.length) : error;
}

export function providerUsageRefreshNotice(snapshot: ProviderUsageSnapshot) {
  const providers = visibleUsageEntries(snapshot.legacy_analytics?.live_usage ?? {});
  if (providers.length === 0) return "Provider usage refresh completed, but no live usage data is available.";

  const current = providers
    .filter(([, envelope]) => !envelope.data?.stale)
    .map(([provider]) => providerLabel(provider));
  const stale = providers
    .filter(([, envelope]) => envelope.data?.stale)
    .map(([provider, envelope]) => `${providerLabel(provider)} — ${conciseRefreshError(provider, envelope.data?.refresh_error)}`);

  if (stale.length === 0) return `Provider usage refreshed: ${current.join(", ")}.`;
  return [
    current.length > 0 ? `Updated: ${current.join(", ")}.` : undefined,
    `Still stale: ${stale.join("; ")}.`,
  ].filter(Boolean).join(" ");
}

export function providerUsageStaleDetail(provider: string, error?: string) {
  return `stale: ${conciseRefreshError(provider, error)}`;
}

export async function requestProviderUsageRefresh<T>(request: FetchLike = fetch): Promise<T> {
  const response = await request("/api/v1/runtime/provider-usage/refresh", { method: "POST" });
  const body = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) {
    throw new Error(body.error ?? `Provider usage refresh failed (${response.status})`);
  }
  return body;
}

export function ProviderUsageRefreshButton({
  refreshing,
  onRefresh,
}: {
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const label = refreshing ? "Refreshing provider usage" : "Refresh provider usage";
  const title = refreshing ? label : "Refresh enabled provider usage manually";
  return <button
    type="button"
    className={`provider-usage-refresh${refreshing ? " refreshing" : ""}`}
    aria-label={label}
    title={title}
    disabled={refreshing}
    onClick={onRefresh}
  >
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none">
      <path d="M16 6.5V3m0 0h-3.5M16 3l-2.2 2.2a6 6 0 1 0 1.6 6.1" />
    </svg>
  </button>;
}
